// @ts-strict-ignore
import { IonItem, IonLabel, IonList, IonPopover } from '@ionic/react'
import { ionHistory } from 'mobile_web/modules/navigation'
import NavigationService from 'mobile_web/modules/navigation/NavigationService'
import {
	formatLanguageUrl,
	getScannerUrl,
	getSettingsUrl,
	getProfileUrl,
	getTabIndex,
	SCANNER_PAGE_SLUG,
	SETTINGS_PAGE_SLUG,
	PROFILE_PAGE_SLUG,
} from 'mobile_web/modules/navigation/routes'
import { getDirMode } from 'mobile_web/modules/platform'
import ScannerIcon from 'mobile_web/modules/scanner/components/parts/ScannerIcon'
import { getScannerLabel } from 'mobile_web/modules/scanner/functions'
import { TabConfig } from 'mytours-read-common'
import React, { useMemo } from 'react'
import { tabsCount } from '../functions'
import { removeFullWidthFromTabs } from 'mobile_web/modules/map/functions'
import classNames from 'classnames'
import TabIcon from './TabIcon'
import strings from 'mobile_web/strings'
import Settings from '../../settings/functions'

interface Props {
	keypadEnabled: boolean
	qrEnabled: boolean
	searchEnabled: boolean
	togglePopover: (event?) => void
	isSmallMobile: boolean
	isMobile: boolean
	isBigMobile: boolean
	tabs: TabConfig[]
	showScanner: boolean
	isPopoverVisible: boolean
	floorPopoverEvent: Event
}

const MorePopover: React.FC<Props> = ({
	keypadEnabled,
	qrEnabled,
	searchEnabled,
	togglePopover,
	isSmallMobile,
	isMobile,
	isBigMobile,
	tabs,
	showScanner,
	isPopoverVisible,
	floorPopoverEvent,
}) => {
	const scannerTab = useMemo(
		() =>
			showScanner && (
				<IonItem
					key={SCANNER_PAGE_SLUG}
					detail={false}
					className="tab-button"
					onClick={() => {
						ionHistory.push(getScannerUrl())
						removeFullWidthFromTabs()
						togglePopover()
					}}
				>
					<div className="tab-button-inner">
						<ScannerIcon keypadEnabled={keypadEnabled} qrEnabled={qrEnabled} searchEnabled={searchEnabled} className="tab-icon" />
						<IonLabel>{getScannerLabel({ keypadEnabled, qrEnabled, searchEnabled })}</IonLabel>
					</div>
				</IonItem>
			),
		[keypadEnabled, qrEnabled, showScanner, searchEnabled, Settings?.appLanguage?.currentLocale],
	)

	const settingsTab = useMemo(
		() => (
			<IonItem
				key={SETTINGS_PAGE_SLUG}
				detail={false}
				className="tab-button"
				onClick={() => {
					ionHistory.push(getSettingsUrl())
					removeFullWidthFromTabs()
					togglePopover()
				}}
			>
				<div className="tab-button-inner">
					<TabIcon tabBarIcon={'gear'} />
					<IonLabel>{strings.settings}</IonLabel>
				</div>
			</IonItem>
		),
		[Settings?.appLanguage?.currentLocale],
	)

	const profileTab = useMemo(
		() => (
			<IonItem
				key={PROFILE_PAGE_SLUG}
				detail={false}
				className="tab-button"
				onClick={() => {
					ionHistory.push(getProfileUrl())
					removeFullWidthFromTabs()
					togglePopover()
				}}
			>
				<div className="tab-button-inner">
					<TabIcon tabBarIcon={'user'} />
					<IonLabel>{strings.profile}</IonLabel>
				</div>
			</IonItem>
		),
		[Settings?.appLanguage?.currentLocale],
	)

	const tabsLength = tabsCount(tabs.length, { isSmallMobile, isMobile, isBigMobile })

	const moreTabs = useMemo(() => tabs?.slice(tabsLength) || [], [tabs, tabsLength])

	const activeIndex = NavigationService.navigationStack.getActiveTabIndex()

	return (
		<IonPopover
			className="floor-popover-more"
			isOpen={isPopoverVisible}
			onDidDismiss={() => togglePopover()}
			showBackdrop={false}
			event={floorPopoverEvent}
			mode="md"
		>
			<IonList dir={getDirMode()} className='popover-content'>
				{moreTabs.map((tab: TabConfig, index: number) => {
					const tabIndex = tabsLength + index

					if (tab.key === SCANNER_PAGE_SLUG) {
						return scannerTab
					} else if (tab.key === SETTINGS_PAGE_SLUG) {
						return settingsTab
					} else if (tab.key === PROFILE_PAGE_SLUG) {
						return profileTab
					}

					return (
						<IonItem
							key={index}
							onClick={() => {
								ionHistory.push(formatLanguageUrl(`/${getTabIndex(tabIndex)}`))
								NavigationService.navigationStack.setActiveTabIndex(tabIndex + 1)
								removeFullWidthFromTabs()
								togglePopover()
							}}
							className={classNames('tab-button', Number(activeIndex) === tabIndex + 1 && 'active')}
							detail={false}
							lines="none"
						>
							<div className="tab-button-inner">
								<TabIcon tabBarIcon={tab.tabBarIcon} />
								<IonLabel>{tab.tabBarTitle || '_'}</IonLabel>
							</div>
						</IonItem>
					)
				})}
			</IonList>
		</IonPopover>
	)
}

export default MorePopover
